// src/components/principal/SubscriptionPanel.jsx (v4 – handles loading/unknown + better logic)

import React, { useEffect, useState } from "react";

import useSubscription, { formatLocalDate } from "../../hooks/useSubscription";
import useStripeActions from "../../hooks/useStripeActions";
import { STRIPE_PUBLISHABLE_KEY, DEFAULT_PRICE_ID } from "../../config";

/**
 * Subscription management panel for principals.
 *
 * Displays current status (trialing, active, canceling, inactive)
 * and provides actions to subscribe or manage via Stripe Customer Portal.
 *
 * @component
 * @returns {JSX.Element}
 */
const SubscriptionPanel = () => {
  const { status, meta, refresh, loading: subLoading, error: subError } =
    useSubscription({ enabled: true });

  const { startSubscription, openCustomerPortal, loading: stripeLoading } =
    useStripeActions({
      publishableKey: STRIPE_PUBLISHABLE_KEY,
      priceId: DEFAULT_PRICE_ID,
    });

  const [showStripeOverlay, setShowStripeOverlay] = useState(false);
  const [localError, setLocalError] = useState("");

  // Show overlay only after brief delay (Stripe actions only)
  useEffect(() => {
    if (!stripeLoading) {
      setShowStripeOverlay(false);
      return;
    }
    const id = setTimeout(() => setShowStripeOverlay(true), 400);
    return () => clearTimeout(id);
  }, [stripeLoading]);

  const canUsePortal = Boolean(meta?.stripe_subscription_id);

  // “Effective” status: sometimes status might be UNKNOWN due to a transient fetch issue,
  // but we still want to avoid telling the user “No subscription” if we can see a Stripe sub id.
  const effectiveStatus =
    status === "UNKNOWN" && canUsePortal ? "ACTIVE" : status;

  const handleStartSubscription = async () => {
    setLocalError("");
    try {
      await startSubscription();
      await refresh();
    } catch (err) {
      console.error("Subscription start error:", err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to start subscription. Please try again."
      );
    }
  };

  const handleOpenPortal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalError("");
    try {
      await openCustomerPortal();
    } catch (err) {
      console.error("Portal open error:", err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to open Customer Portal. Please try again."
      );
    }
  };

  /**
   * @param {{children: React.ReactNode}} props
   */
  const PortalLinkButton = ({ children }) => (
    <button type="button" onClick={handleOpenPortal} className="link-button">
      {children}
    </button>
  );

  /**
   * @param {{visible: boolean}} props
   */
  const StripeOverlay = ({ visible }) =>
    !visible ? null : (
      <div className="stripe-overlay">
        <div className="stripe-overlay-backdrop" />
        <div className="stripe-overlay-content">
          <p>Connecting securely to Stripe…</p>
        </div>
      </div>
    );

  // === LOADING / UNKNOWN (don’t show “No subscription” yet) ===
  if (subLoading || effectiveStatus === "UNKNOWN") {
    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        <p className="no-subscription-message">Checking subscription status…</p>

        {/* If it failed to load, show a helpful message instead of implying “inactive”. */}
        {subError && (
          <p className="error-message">
            We couldn’t load your subscription status just now. Please refresh the page
            (or try again in a moment).
          </p>
        )}

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === TRIALING ===
  if (effectiveStatus === "TRIALING") {
    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        {canUsePortal ? (
          <p>
            Your 7-day free trial is active. Complete payment in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton> anytime before
            it ends to keep your account and data.
          </p>
        ) : (
          <>
            <p className="no-subscription-message">
              Your school is in a 7-day free trial. Subscribe now to keep access
              and data after the trial, or continue and subscribe later.
            </p>
            <button
              onClick={handleStartSubscription}
              disabled={stripeLoading}
              className="btn btn-primary subscription-button"
            >
              Exit Trial and Subscribe
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === ACTIVE (not canceling) ===
  if (effectiveStatus === "ACTIVE" && !meta?.subscription_cancel_at_period_end) {
    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        {canUsePortal ? (
          <p>
            Your subscription is active. Manage it in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton>.
          </p>
        ) : (
          <>
            <p>Your subscription is active.</p>
            <button
              onClick={handleStartSubscription}
              disabled={stripeLoading}
              className="btn btn-primary subscription-button"
            >
              Update Billing
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === ACTIVE (canceling) ===
  if (effectiveStatus === "ACTIVE" && meta?.subscription_cancel_at_period_end) {
    const endISO =
      meta.subscription_current_period_end_iso ||
      meta.subscription_cancel_at_iso ||
      null;

    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        <p>
          <strong>Canceling:</strong> Your subscription ends on{" "}
          <strong>{formatLocalDate(endISO)}</strong>. Change this in the{" "}
          {canUsePortal ? (
            <PortalLinkButton>Customer Portal</PortalLinkButton>
          ) : (
            "Customer Portal"
          )}
          .
        </p>

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === INACTIVE ===
  return (
    <div className="principal-panel subscription-content">
      <h3>Manage Subscription</h3>
      <p className="no-subscription-message">
        No active subscription. Subscribe to enable staff uploads and keep your
        school’s accounts and data.
      </p>
      <button
        onClick={handleStartSubscription}
        disabled={stripeLoading}
        className="btn btn-primary subscription-button"
      >
        Subscribe to GhrianGhraffy
      </button>

      {localError && <p className="error-message">{localError}</p>}
      <StripeOverlay visible={showStripeOverlay} />
    </div>
  );
};

export default SubscriptionPanel;
